import {
	closestCenter,
	DndContext,
	DndContextProps,
	DragOverlay,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	horizontalListSortingStrategy,
	SortableContext,
	sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { css } from '@emotion/react'
import { findIndex, partial } from 'lodash-es'
import { useState, type HTMLAttributes } from 'react'
import { Gallery, Item } from 'react-photoswipe-gallery'
import { AttachmentType, FileAttachment as FileAttachmentType } from '../../states/attachment'
import AttachmentSortContainer from './AttachmentSortContainer'
import FileAttachment from './FileAttachment'

export interface FileAttachmentGroupProps extends HTMLAttributes<HTMLDivElement> {
	data: FileAttachmentType[]
	onDelete?: (id: string) => void
	onSwap?: (oldIndex: number, newIndex: number) => void
}

/**
 * 展示附件
 */
export default function FileAttachmentGroup({
	data,
	onDelete,
	onSwap,
	...props
}: FileAttachmentGroupProps) {
	const [draggingItem, setDraggingItem] = useState<FileAttachmentType | null | undefined>(null)
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	)
	const handleDragStart: DndContextProps['onDragStart'] = (event) => {
		setDraggingItem(data.find((item) => item.id === event.active.id))
	}

	const handleDragEnd: DndContextProps['onDragEnd'] = (event) => {
		const { active, over } = event

		if (over?.id && active.id !== over.id) {
			const oldIndex = findIndex(data, (item) => item.id === active.id)
			const newIndex = findIndex(data, (item) => item.id === over.id)

			onSwap?.(oldIndex, newIndex)
		}
		setDraggingItem(null)
	}

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
		>
			<SortableContext items={data} strategy={horizontalListSortingStrategy}>
				<Gallery>
					<div
						css={css`
							display: flex;
							align-items: center;
							gap: 10px;
							overflow: auto hidden;

							&::-webkit-scrollbar {
								display: none;
							}
						`}
						{...props}
					>
						{data.map((item: FileAttachmentType, index: number) => {
							const content =
								item.type === AttachmentType.VIDEO ? (
									<video
										src={item.cdn?.file.fileUrl}
										width={item.metadata?.width}
										height={item.metadata?.height}
										controls
										muted
										playsInline
										autoPlay
										css={css`
											width: 100%;
											height: 100%;
											display: block;
											margin: auto;
										`}
									/>
								) : undefined

							return (
								<AttachmentSortContainer id={item.id} key={item.id}>
									<Item
										thumbnail={item.cdn?.file.fileUrl}
										original={item.cdn?.file.fileUrl}
										width={item.metadata?.width}
										height={item.metadata?.height}
										content={content}
									>
										{({ ref, open }) => (
											<FileAttachment
												ref={ref}
												onDelete={onDelete && partial(onDelete, item.id)}
												data={item}
												onClick={open}
											/>
										)}
									</Item>
								</AttachmentSortContainer>
							)
						})}
					</div>
				</Gallery>
			</SortableContext>
			<DragOverlay>{draggingItem ? <FileAttachment data={draggingItem} /> : null}</DragOverlay>
		</DndContext>
	)
}
